import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { filter, map, take } from 'rxjs/operators';
import { LoadAddedValuesAction } from '../../../actions/added-values.actions';
import { generateAddedValueFormName } from '../../../helpers/form-added-value-name-generator';
import { AddedValueFieldConfig } from '../../../models/form-added-value-field-config';
import { getAvailableLegacyVapsAndProducts, productAddedValues } from '../../../selectors/added-value.selectors';
import { getRmsRehydrate } from '../../../selectors';
import { RmsSpecificationRemoveRehydrateItem } from '../../../actions/rms-specification.actions';
import { getAvailableProducts } from '../../../selectors/products.selectors';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

const productsAndRehydrate = createSelector(getAvailableProducts, getRmsRehydrate, (products, rehydrate) => {
  return {
    products,
    rehydrate,
  };
});

const range = (n) => Array.from({ length: n }, (value, key) => key);

const isRehydratation = (
  id: number,
  rehydrateState: {
    [id: number]: {
      [key: string]: any;
    };
  },
  config: AddedValueFieldConfig,
  fieldName = config.name,
) => {
  if (rehydrateState && rehydrateState[id] && config.rehydrate) {
    const rehydratationValue = _safe(() => rehydrateState[id][fieldName]);
    return !!rehydratationValue || fieldName in rehydrateState[id];
  } else {
    return false;
  }
};

/**
 * Loads the product added values
 * This only applies the first time added values loads (To the first value added dropdown)
 */
@Injectable()
export class LoadProductAddedValues implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(
    value: string,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.store
      .select(productsAndRehydrate)
      .pipe(
        filter((state) => !!state.products && state.products.length > 0),
        take(1),
      )
      .subscribe((state) => {
        if (isRehydratation(form.data.id, state.rehydrate, config, generateAddedValueFormName(0))) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
        } else if (value) {
          this.store.dispatch(
            new LoadAddedValuesAction({
              productCode: value,
              index: 0,
            }),
          );
        }
      });
  }
}

@Injectable()
export class LoadChildAddedValues implements OnFieldChanged {
  vapsBuffer = { 0: undefined, 1: undefined, 2: undefined, 3: undefined };

  constructor(private store: Store<OrderBuilderState>) {}

  private filterRehydrate(form: ConfigurableFormComponent, config: AddedValueFieldConfig) {
    return this.store.select(productsAndRehydrate).pipe(
      take(1),
      filter((rehydrateState) => {
        if (isRehydratation(form.data.id, rehydrateState, config)) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
          return false;
        } else {
          return true;
        }
      }),
    );
  }

  private accAddedValues(productId: any, formGroup: UntypedFormGroup, config: AddedValueFieldConfig) {
    return this.store.pipe(
      select(productAddedValues(productId)),
      filter((tuples) => tuples && !!tuples.length),
      take(1),
      map((tuples) => {
        // Accomulate added values
        return range(config.index + 1).map((_, index) => {
          const family = formGroup.get(generateAddedValueFormName(index)).value;
          if (tuples[index] && tuples[index].addedValues) {
            return tuples[index].addedValues.find((adddedValue) => adddedValue.family === family);
          } else {
            return null;
          }
        });
      }),
      filter((addedValue) => !!addedValue && !!addedValue.length),
    );
  }

  onFieldChanged(
    value: number,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.store
      .select(getAvailableLegacyVapsAndProducts)
      .pipe(
        filter((state) => state.availableProducts.length > 0),
        filter(
          (state) =>
            (config.index === 0 && Object.keys(state.availableAddedValues).length === 0) ||
            Object.keys(state.availableAddedValues).length > 0,
        ),
        take(1),
      )
      .subscribe(() => {
        if (value !== this.vapsBuffer[config.index]) {
          this.vapsBuffer[config.index] = value;

          this.filterRehydrate(form, config).subscribe(() => {
            // Guard
            const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
            if (config.limit || !productCode) {
              return;
            }

            // Case when unselecting first added value
            if (!value && config.index === 0) {
              this.store.dispatch(
                new LoadAddedValuesAction({
                  productCode: productCode,
                  index: 0,
                }),
              );
              return;
            }

            // Get entire selected object
            this.accAddedValues(productCode, formGroup, config).subscribe((addedValues) => {
              if (productCode) {
                // Unselect next one to avoid auto-selection when fetching finishes
                const control = formGroup.get(generateAddedValueFormName(config.index + 1));
                const userFormControl = formGroup.get(generateAddedValueFormName(config.index));
                if (userFormControl.dirty && control && control.value) {
                  control.setValue(undefined);
                }

                if (!!addedValues[0]) {
                  this.store.dispatch(
                    new LoadAddedValuesAction({
                      productCode: productCode,
                      index: config.index + 1,
                      addedValues: addedValues,
                    }),
                  );
                }
              }
            });
          });
        }

        return;
      });
  }
}
